import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Divider, IconButton, makeStyles, Typography } from '@material-ui/core';
import { PaymentsTypeLabels } from 'modules/pos-settings/constants/paymentMethods';
import { AS_PAID_INVOICE_PAYMENT_SPLIT_STATUS, PaymentOptionTypes } from '../constants';
import { IInvoicePaymentSplit, PaymentsType } from 'common/interfaces/invoices';
import { CustomTheme } from 'common/ui/interfaces';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import { formatNumberToPrice } from 'common/utils/format';
import { ReactComponent as EditIcon } from 'img/icons/pencil_deprecated.svg';
import componentMessages from '../messages';
import messages from 'common/messages/messages';
import { creditCardTypesLabels } from 'common/components/CreditCardData/constants';

const useStyles = makeStyles((theme: CustomTheme) => ({
  totalsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  totalLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  editTotalButton: {
    marginRight: theme.spacing(1),
    color: theme.palette.secondary.main,

    '& svg': {
      width: 16,
      height: 16,
    },
  },
  footerTotalText: {
    fontSize: '1.125rem',
    fontWeight: 700,
  },
  footerTotalAmount: {
    fontSize: '1.125rem',
    fontWeight: 700,
  },
  addTaxButton: {
    fontWeight: 300,
  },
  paymentsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    maxHeight: 100,
    overflowY: 'auto',
  },
}));

interface IInvoiceTotalSubtotalComponent {
  payments: Array<IInvoicePaymentSplit>;
  discount?: number | null;
  leftToPay?: number;
  subtotal: number;
  tax: number;
  total: number;
  isCheckOutResponse?: boolean;
  onEditTotal?: () => void;
  onAddDiscount?: () => void;
  onAddTips?: () => void;
  isMemberPortal?: boolean;
  discountAmount?: number | null;
  disableAddDiscount?: boolean;
  tips?: number | null;
}

export default function InvoiceTotals({
  payments,
  discount,
  leftToPay,
  subtotal,
  tax,
  total,
  isCheckOutResponse = false,
  onEditTotal,
  onAddDiscount,
  onAddTips,
  isMemberPortal,
  discountAmount,
  disableAddDiscount,
  tips,
}: IInvoiceTotalSubtotalComponent): JSX.Element {
  const renderIntlMessage = useRenderIntlMessage();
  const classes = useStyles(isCheckOutResponse);

  const renderTotalItem = (totalLabel: string, value: string) => (
    <Typography component="p" className={classes.totalLabel}>
      <Typography component="span" color="textSecondary">
        {totalLabel}
      </Typography>

      <Typography component="span">{value}</Typography>
    </Typography>
  );

  const getPaymentNumber = (payment: IInvoicePaymentSplit): string => {
    if (payment.type === PaymentsType.CHECKING_SAVINGS && payment.routingNumber) {
      return payment.routingNumber;
    }

    if (payment.type === PaymentsType.CREDIT_CARD && payment.lastFour) {
      return `(**** ${payment.lastFour})`;
    }

    return '';
  };

  const getPaymentTypeLabel = (payment: IInvoicePaymentSplit) => {
    if (payment.creditCardType) {
      return renderIntlMessage(creditCardTypesLabels.message(payment.creditCardType));
    }

    return payment.type ? PaymentsTypeLabels[payment.type] : '';
  };

  return (
    <Box className={classes.totalsWrapper}>
      {renderTotalItem(
        `${renderIntlMessage(componentMessages.subTotal)}:`,
        `$${formatNumberToPrice(subtotal)}`,
      )}
      {renderTotalItem(
        `${renderIntlMessage(componentMessages.tax)}:`,
        `$${formatNumberToPrice(tax || 0)}`,
      )}
      {Boolean(!isCheckOutResponse || (isCheckOutResponse && discount)) && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography color="textSecondary">
            {`${renderIntlMessage(componentMessages.discount)} ${discount || 0}%`}
          </Typography>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            {!isMemberPortal && !isCheckOutResponse && onAddDiscount ? (
              <IconButton
                onClick={onAddDiscount}
                size="small"
                color="primary"
                className={classes.editTotalButton}
                disabled={disableAddDiscount || !!payments?.length}
              >
                <EditIcon />
              </IconButton>
            ) : null}

            <Typography color="textSecondary">
              {discountAmount ? '-' : ''}${formatNumberToPrice(discountAmount || 0)}
            </Typography>
          </Box>
        </Box>
      )}
      {Boolean(!isCheckOutResponse || (isCheckOutResponse && tips)) && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography color="textSecondary">{renderIntlMessage(messages.tips)}</Typography>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            {!isCheckOutResponse && onAddTips ? (
              <IconButton
                onClick={onAddTips}
                size="small"
                color="primary"
                className={classes.editTotalButton}
                disabled={!!payments?.length}
              >
                <EditIcon />
              </IconButton>
            ) : null}

            <Typography color="textSecondary">${formatNumberToPrice(tips || 0)}</Typography>
          </Box>
        </Box>
      )}
      <Box className={classes.totalLabel}>
        <Typography component="p" color="textSecondary" className={classes.footerTotalText}>
          {`${renderIntlMessage(componentMessages.total)}:`}
        </Typography>

        <Box display="flex" alignItems="center">
          {!!onEditTotal && (
            <IconButton onClick={onEditTotal} size="small" className={classes.editTotalButton}>
              <EditIcon />
            </IconButton>
          )}

          <Typography component="span" className={classes.footerTotalAmount}>
            ${formatNumberToPrice(total || 0)}
          </Typography>
        </Box>
      </Box>
      <Divider />
      {!!payments?.length && (
        <Box className={classes.paymentsWrapper}>
          {payments
            .filter(p =>
              p.status ? AS_PAID_INVOICE_PAYMENT_SPLIT_STATUS.includes(p.status) : null,
            )
            .map(payment => (
              <Typography component="p" className={classes.totalLabel} key={payment.id}>
                <Typography component="span" color="textSecondary">
                  <FormattedMessage
                    {...componentMessages.paidWithTitle}
                    values={{
                      title: getPaymentTypeLabel(payment),
                      additionalInfo: payment.creditCardPaymentOptionType
                        ? PaymentOptionTypes[payment.creditCardPaymentOptionType]
                        : '',
                    }}
                  />
                  {` ${getPaymentNumber(payment)}`}
                </Typography>

                <Typography component="span">
                  {payment.type === PaymentsType.MEMBER_REWARDS
                    ? payment.rewardAmount
                    : `$${formatNumberToPrice(payment.paymentAmount)}`}
                </Typography>
              </Typography>
            ))}

          {!isCheckOutResponse &&
            leftToPay &&
            leftToPay >= 0 &&
            renderTotalItem(
              `${renderIntlMessage(componentMessages.leftToPay)}:`,
              `$${formatNumberToPrice(leftToPay)}`,
            )}
        </Box>
      )}
    </Box>
  );
}
